import { Controller } from "@hotwired/stimulus"

// Disables submit until required fields and at least one selected prestacion are present.
export default class extends Controller {
  static targets = [
    "submit",
    "solicitanteHidden",
    "solicitanteInput",
    "solicitanteExterno",
    "matriculaExterna",
    "solicitanteExternoCheck",
    "solicitanteRegistradoFields",
    "solicitanteExternoFields",
    "patientWeight",
    "patientHeight",
    "patientWeightLabel",
    "patientHeightLabel",
    "modality"
  ]

  static values = {
    fromTurnero: Boolean
  }

  connect() {
    this.validate = this.validate.bind(this)
    this.isValidating = false

    this.element.addEventListener("input", this.validate)
    this.element.addEventListener("change", this.validate)

    this.observer = new MutationObserver(() => this.validate())
    this.observer.observe(this.element, { childList: true, subtree: true })

    this.runValidation(() => {
      this.syncSolicitanteFieldsVisibility()
      this.syncWeightHeightRequiredState()
    })
  }

  disconnect() {
    this.element.removeEventListener("input", this.validate)
    this.element.removeEventListener("change", this.validate)

    if (this.observer) {
      this.observer.disconnect()
    }
  }

  toggleSolicitanteExterno() {
    if (this.usingSolicitanteExterno()) {
      this.clearSolicitanteRegistrado()
    } else {
      this.clearSolicitanteExternoFields()
    }

    this.syncSolicitanteFieldsVisibility()
    this.validate()
  }

  selectSolicitanteRegistrado() {
    if (this.hasSolicitanteExternoCheckTarget) {
      this.solicitanteExternoCheckTarget.checked = false
    }

    this.clearSolicitanteExternoFields()
    this.syncSolicitanteFieldsVisibility()
    this.validate()
  }

  validate() {
    if (!this.hasSubmitTarget) return

    this.runValidation(() => {
      this.syncWeightHeightRequiredState()
    })
  }

  runValidation(syncDomState = () => {}) {
    if (this.isValidating) return
    if (!this.hasSubmitTarget) return

    this.isValidating = true
    this.observer?.disconnect()

    try {
      syncDomState()

      const formValid = this.element.checkValidity()
      const hasPrestaciones = this.selectedPrestacionesCount() > 0
      const solicitanteOk = this.solicitanteCompleto()
      const weightHeightOk = this.weightHeightOk()

      this.submitTarget.disabled = !(formValid && hasPrestaciones && solicitanteOk && weightHeightOk)
    } finally {
      this.observer?.observe(this.element, { childList: true, subtree: true })
      this.isValidating = false
    }
  }

  tomografiaModality(modality) {
    return modality.toLowerCase().includes("tomografia")
  }

  currentModality() {
    if (this.hasModalityTarget) {
      return this.modalityTarget.value.trim()
    }

    const modalityField = this.element.querySelector("#orden_de_servicio_modality")
    return modalityField?.value?.trim() || ""
  }

  weightHeightRequired() {
    if (!this.hasPatientWeightTarget && !this.hasPatientHeightTarget) return false

    return this.tomografiaModality(this.currentModality())
  }

  weightHeightOk() {
    if (!this.weightHeightRequired()) return true

    const decimalPattern = /^\d+(?:[.,]\d+)?$/
    const weight = this.hasPatientWeightTarget ? this.patientWeightTarget.value.trim() : ""
    const height = this.hasPatientHeightTarget ? this.patientHeightTarget.value.trim() : ""

    return decimalPattern.test(weight) && decimalPattern.test(height)
  }

  syncWeightHeightRequiredState() {
    const required = this.weightHeightRequired()
    const weightLabel = required ? "Peso *" : "Peso"
    const heightLabel = required ? "Talla *" : "Talla"

    if (this.hasPatientWeightTarget && this.patientWeightTarget.required !== required) {
      this.patientWeightTarget.required = required
    }

    if (this.hasPatientHeightTarget && this.patientHeightTarget.required !== required) {
      this.patientHeightTarget.required = required
    }

    if (this.hasPatientWeightLabelTarget && this.patientWeightLabelTarget.textContent !== weightLabel) {
      this.patientWeightLabelTarget.textContent = weightLabel
    }

    if (this.hasPatientHeightLabelTarget && this.patientHeightLabelTarget.textContent !== heightLabel) {
      this.patientHeightLabelTarget.textContent = heightLabel
    }
  }

  solicitanteCompleto() {
    if (this.usingSolicitanteExterno()) {
      const nombre = this.hasSolicitanteExternoTarget && this.solicitanteExternoTarget.value.trim() !== ""
      const matricula = this.hasMatriculaExternaTarget && this.matriculaExternaTarget.value.trim() !== ""

      return nombre && matricula
    }

    return this.hasSolicitanteHiddenTarget && this.solicitanteHiddenTarget.value.trim() !== ""
  }

  usingSolicitanteExterno() {
    return this.hasSolicitanteExternoCheckTarget && this.solicitanteExternoCheckTarget.checked
  }

  syncSolicitanteFieldsVisibility() {
    const externo = this.usingSolicitanteExterno()

    if (this.hasSolicitanteRegistradoFieldsTarget) {
      this.solicitanteRegistradoFieldsTarget.classList.toggle("d-none", externo)
    }

    if (this.hasSolicitanteExternoFieldsTarget) {
      this.solicitanteExternoFieldsTarget.classList.toggle("d-none", !externo)
    }
  }

  clearSolicitanteExternoFields() {
    if (this.hasSolicitanteExternoTarget) {
      this.solicitanteExternoTarget.value = ""
    }

    if (this.hasMatriculaExternaTarget) {
      this.matriculaExternaTarget.value = ""
    }
  }

  clearSolicitanteRegistrado() {
    if (this.hasSolicitanteHiddenTarget) {
      this.solicitanteHiddenTarget.value = ""
    }

    if (this.hasSolicitanteInputTarget) {
      this.solicitanteInputTarget.value = ""
    }
  }

  selectedPrestacionesCount() {
    const wrappers = Array.from(this.element.querySelectorAll(".imagen-prestaciones-form-wrapper"))

    return wrappers.filter((wrapper) => {
      if (wrapper.closest("template")) return false
      if (wrapper.hidden || wrapper.classList.contains("d-none")) return false

      const destroyInput = wrapper.querySelector('input[name*="[_destroy]"]')
      if (destroyInput && (destroyInput.value === "1" || destroyInput.value === "true")) return false

      const codeStudy =
        wrapper.querySelector('input[name$="[code_study]"]')?.value?.trim() ||
        wrapper.querySelector('[data-linked-autocomplete-target="codeStudyInput"]')?.value?.trim() ||
        ""
      const study =
        wrapper.querySelector('input[name$="[study]"]')?.value?.trim() ||
        wrapper.querySelector('[data-linked-autocomplete-target="studyInput"]')?.value?.trim() ||
        ""

      return codeStudy !== "" || study !== ""
    }).length
  }
}
